import {
  AdditionalFee,
  Heating,
  HeatingProduct,
  LogoPrice,
  Order,
  OrderDeliveryPreferences,
  OrderLine,
  OrderLineDesign,
  OrderLineDesignInstance,
  OrderLineDesignInstanceLogo,
  OrderLineLogo,
} from "../../../core/models/entities/Order";
import {
  AdditionalFeeDto,
  DesignInstanceDto,
  DesignInstanceLogoDto,
  HeatingDto,
  HeatingProductDto,
  LogoPriceDto,
  OrderDto,
  OrderLineDto,
} from "../responses/OrderDto";
import {
  CreateOrderDto,
  CreateOrderPreferencesDto,
  OrderProductDto,
  OrderProductLogoDto,
  OrderProductDesignDto,
} from "../../../core/models/dtos/CreateOrderDto";
import {
  CreateOrderLine,
  CreateOrderLineDesign,
  CreateOrderLineLogo,
} from "../bodies/CreateOrder";
import { mapToDesign, mapToPrice } from "./mapDesign";
import { mapToLogo } from "./mapLogo";
import { DesignSize } from "../../../presentation/screens/order-create/models/CartItem";
import { mapToOrganization } from "./mapOrganization";
import { mapToApprovalGroup } from "./ApprovalMapper";
import { DesignDto } from "../responses/DesignDto";
import { LogoDto } from "../responses/LogoDto";

export function mapToOrder(dto: OrderDto): Order {
  return {
    id: dto.id,
    vat: dto.vat,
    total: dto.total,
    subTotal: dto.subTotal,
    latestDelivery: new Date(dto.latestDelivery),
    createdAtUtc: new Date(dto.createdAtUtc),
    updatedAtUtc: new Date(dto.updatedAtUtc),
    placedAtUtc: new Date(dto.placedAtUtc),
    orderNumber: dto.orderNumber,
    price: dto.price,
    currency: dto.currency,
    status: dto.status,
    lines: dto.lines?.map(mapToOrderLine) ?? [],
    additionalFees: dto.additionalFees?.map(mapToAdditionalFee) ?? [],
    heating: dto.heating?.map(mapHeating) ?? [],
    logoPrices: dto.logoPrices?.map(mapLogoPrice) ?? [],
    deliveryType: dto.deliveryType,
    orderComment: dto.orderComment,
    originOfDelivery: dto.originOfDelivery,
    requisitionNumber: dto.requisitionNumber,
    packagingType: dto.packagingType,
    organization: mapToOrganization(dto.organization),
    approval: dto.approval ? mapToApprovalGroup(dto.approval) : null,
    orderDeliveryAddress: { ...dto.orderDeliveryAddress },
    deliveryBefore12PMwithGLS: dto.deliveryBefore12PMwithGLS,
    stripePaymentUrl: dto.stripePaymentUrl,
    stripePaymentStatus: dto.stripePaymentStatus,
  };
}

function mapHeating(dto: HeatingDto): Heating {
  return {
    ...dto,
    product: mapHeatingProduct(dto.product),
    quantity: dto.quantity,
  };
}

export function mapLogoPrice(dto: LogoPriceDto): LogoPrice {
  return {
    ...dto,
  };
}

function mapHeatingProduct(dto: HeatingProductDto): HeatingProduct {
  return {
    ...dto,
    additionalFee: dto.additionalFee
      ? mapToAdditionalFee(dto.additionalFee)
      : undefined,
    prices: dto.prices?.map((x) => mapToPrice(x)) ?? [],
  };
}

export function mapToOrderLine(dto: OrderLineDto): OrderLine {
  switch (dto.product?.productType) {
    case "Logo":
      return mapOrderLineLogo(dto);
    case "Design":
      return mapOrderLineDesign(dto);
    default:
      return mapOrderLine(dto);
  }
}

export function mapOrderLine(dto: OrderLineDto): OrderLine {
  const subject: LogoDto | DesignDto = dto.logo ?? dto.design;
  return {
    displayImage:
      subject?.displayImage?.lowResUrl ?? subject?.displayImage?.lowResUrl,
    id: dto.id,
    displayNo: dto.displayNo,
    title: subject?.title,
    quantity: dto.product?.quantity,
    price: dto.product.price,
    unitPrice: dto.product.unitPrice,
    adjustedPrice: dto.product.adjustedPrice,
    adjustedUnitPrice: dto.product.adjustedUnitPrice,
    toDelivery: dto.product.toDelivery,
    toStorage: dto.product.toStorage,
    type: dto.product.productType,
    relatedTo: dto.relatedTo,
    casing: dto.casing,
    product: {
      productNo: dto.product.productNo,
      productSku: subject?.product.sku,
      productName: dto.product.productName,
      productType: dto.product.productType,
    },
    deliveryType: dto.type.value,
    additionalFees: dto.additionalFees?.map(mapToAdditionalFee) ?? [],
  };
}

function mapOrderLineLogo(dto: OrderLineDto): OrderLineLogo {
  const orderLine = mapOrderLine(dto) as OrderLineLogo;
  if (dto.logo) orderLine.baseLogo = mapToLogo(dto.logo);
  return orderLine;
}

function mapOrderLineDesign(dto: OrderLineDto): OrderLineDesign {
  const orderLine = mapOrderLine(dto) as OrderLineDesign;
  if (dto.design) orderLine.design = mapToDesign(dto.design);
  orderLine.instances =
    dto.product?.instances?.map((instance) => mapToDesignInstance(instance)) ??
    [];
  return orderLine;
}

function mapToDesignInstance(
  instanceDto: DesignInstanceDto
): OrderLineDesignInstance {
  return {
    logos: instanceDto.logos.map(mapToDesignInstanceLogo),
    size: instanceDto.size,
    label: "instanceDto.label",
    quantity: instanceDto.toDelivery,
  };
}

function mapToDesignInstanceLogo(
  dto: DesignInstanceLogoDto
): OrderLineDesignInstanceLogo {
  return {
    isNumeric: dto.isNumeric,
    logoId: dto.logoId,
    value: dto.value,
  };
}

export function mapToAdditionalFee(dto: AdditionalFeeDto): AdditionalFee {
  // Sort prices by minQuantity to ensure proper tier ordering
  const sortedPrices = [...dto.prices].sort(
    (a, b) => a.minQuantity - b.minQuantity
  );

  // Use the first price tier as the default values for backward compatibility
  const defaultPrice = sortedPrices[0] ?? {};
  if (defaultPrice?.minQuantity <= 0) defaultPrice.minQuantity = 1;

  // TODO This should be prettier, and price fields should match better.
  return {
    itemNo: dto.itemNo,
    name: dto.name,
    price: defaultPrice.adjustedPrice,
    prices: sortedPrices, // Preserve all pricing tiers, sorted by quantity
    quantity: defaultPrice.minQuantity,
    unitPrice: defaultPrice.adjustedPrice,
  };
}

export function mapToCreateOrderDto(
  orderLines: CreateOrderLine[]
): CreateOrderDto {
  return {
    products: orderLines.map(mapToOrderProductDto),
  };
}

export function mapToCreateOrderDeliveryPreferences(
  orderDeliveryPreferences: OrderDeliveryPreferences,
  products?: OrderProductDto[]
): CreateOrderPreferencesDto {
  return {
    shipToName: orderDeliveryPreferences.company,
    shipToZip: orderDeliveryPreferences.zipCode,
    shipToContactPerson: orderDeliveryPreferences.contactPerson,
    shipToContactEmail: orderDeliveryPreferences.email,
    shipToContactPhone: orderDeliveryPreferences.contactPhone,
    shipToCity: orderDeliveryPreferences.city,
    shipToAddress: orderDeliveryPreferences.address,
    shipToCountry: orderDeliveryPreferences.country,
    emailtrackAndTrace: orderDeliveryPreferences.emailtrackAndTrace,
    latestDeliveryDate: orderDeliveryPreferences.latestDeliveryDate,
    deliveryBefore12PMwithGLS:
      orderDeliveryPreferences.deliveryBefore12PMwithGLS,
    deliveryType: orderDeliveryPreferences.deliveryType,
    packaging: orderDeliveryPreferences.packaging,
    orderComment: orderDeliveryPreferences.orderComment,
    originOfDelivery: orderDeliveryPreferences.originOfDelivery,
    requisitionNumber: orderDeliveryPreferences.requisitionNumber,
    shippingService: orderDeliveryPreferences.shippingService,
    products: products,
  };
}

function mapToOrderProductDto(orderLines: CreateOrderLine): OrderProductDto {
  switch (orderLines.productType) {
    case "Logo": {
      return mapToProductDetailLogoDto(orderLines as CreateOrderLineLogo);
    }
    case "Design": {
      return mapToProductDetailDesignDto(orderLines as CreateOrderLineDesign);
    }
  }
}

function mapToProductDetailLogoDto(
  orderline: CreateOrderLineLogo
): OrderProductDto {
  const dto = {
    productNo: orderline.productId,
    productType: orderline.productType,
    toStorage: orderline.toStorage,
    toDelivery: orderline.toDelivery,
    value: orderline.value,
    isNumeric: orderline.isNumeric,
  } as OrderProductLogoDto;

  return dto;
}

function mapToProductDetailDesignDto(
  orderline: CreateOrderLineDesign
): OrderProductDto {
  const dto = {
    productNo: orderline.productId,
    productType: orderline.productType,
    instances: orderline.instances,
  } as OrderProductDesignDto;

  return dto;
}
